import React, { useState, useRef, useEffect } from "react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import article from "./InvestoContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const CompoundingVisualizerCalculator = () => {
  const content = article.find((item) => item.id === 9);
  const [activeTab, setActiveTab] = useState("visualizer");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [compoundingFrequency, setCompoundingFrequency] = useState("annually");
  const [notifications, setNotifications] = useState([]);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const [timeframe, setTimeframe] = useState("years");

  const [inputs, setInputs] = useState({
    // Basic inputs
    principal: "10000",
    monthlyContribution: "500",
    annualReturn: "8",
    timePeriod: "20",
    compoundingFrequency: "annually",
    
    // Advanced inputs
    inflationRate: "2.5",
    taxRate: "15",
    fees: "0.25",
    contributionGrowth: "3",
    
    // Comparison inputs
    comparePrincipal: "5000",
    compareMonthly: "250",
    compareReturn: "6",
    
    // Goal tracking
    goalAmount: "500000",
    currentAge: "30",
    targetAge: "65"
  });

  const [compoundingData, setCompoundingData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "visualizer" || activeTab === "comparison") {
      calculateCompounding();
    }
  }, [inputs, activeTab, timeframe]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const calculateCompounding = () => {
    const {
      principal,
      monthlyContribution,
      annualReturn,
      timePeriod,
      compoundingFrequency,
      inflationRate,
      taxRate,
      fees,
      contributionGrowth,
      comparePrincipal,
      compareMonthly,
      compareReturn,
      goalAmount,
      currentAge,
      targetAge
    } = inputs;

    if (!principal || !annualReturn) {
      return;
    }

    const principalAmount = parseFloat(principal);
    const monthlyContributionAmount = parseFloat(monthlyContribution || 0);
    const annualReturnRate = parseFloat(annualReturn) / 100;
    const periods = timeframe === "months" ? parseInt(timePeriod) : parseInt(timePeriod) * 12;
    const inflation = parseFloat(inflationRate) / 100;
    const tax = parseFloat(taxRate) / 100;
    const annualFees = parseFloat(fees) / 100;
    const contributionGrowthRate = parseFloat(contributionGrowth) / 100;

    // Calculate compounding periods per year
    let periodsPerYear = 1;
    switch (compoundingFrequency) {
      case "daily": periodsPerYear = 365; break;
      case "monthly": periodsPerYear = 12; break;
      case "quarterly": periodsPerYear = 4; break;
      case "annually": periodsPerYear = 1; break;
      default: periodsPerYear = 1;
    }

    const periodRate = annualReturnRate / periodsPerYear;
    const effectiveAnnualReturn = Math.pow(1 + periodRate, periodsPerYear) - 1;

    // Main calculation
    const data = [];
    let totalValue = principalAmount;
    let totalContributions = principalAmount;
    let totalInterest = 0;
    let currentMonthly = monthlyContributionAmount;

    for (let period = 0; period <= periods; period++) {
      if (period > 0) {
        // Apply compounding for each sub-period in the year
        for (let subPeriod = 0; subPeriod < periodsPerYear; subPeriod++) {
          totalValue = totalValue * (1 + periodRate);
        }
        
        // Apply fees and taxes annually
        if (period % 12 === 0) {
          totalValue = totalValue * (1 - annualFees);
          const annualGrowth = totalValue - totalContributions - totalInterest;
          const taxAmount = annualGrowth * tax;
          totalValue -= taxAmount;
        }
        
        // Add monthly contributions
        totalValue += currentMonthly * 12 / periodsPerYear;
        totalContributions += currentMonthly * 12 / periodsPerYear;
        
        // Increase contributions annually
        if (period % 12 === 0 && period > 0) {
          currentMonthly *= (1 + contributionGrowthRate);
        }
      }

      const realValue = totalValue / Math.pow(1 + inflation, period / 12);
      totalInterest = totalValue - totalContributions;

      data.push({
        period,
        value: totalValue,
        realValue: realValue,
        contributions: totalContributions,
        interest: totalInterest,
        year: period / 12,
        monthlyContribution: currentMonthly
      });
    }

    // Comparison calculation
    const comparePrincipalAmount = parseFloat(comparePrincipal || 0);
    const compareMonthlyAmount = parseFloat(compareMonthly || 0);
    const compareReturnRate = parseFloat(compareReturn || 0) / 100;

    if (comparePrincipalAmount > 0 || compareMonthlyAmount > 0) {
      const compareData = [];
      let compareValue = comparePrincipalAmount;
      let compareContributions = comparePrincipalAmount;
      let compareInterest = 0;

      for (let period = 0; period <= periods; period++) {
        if (period > 0) {
          for (let subPeriod = 0; subPeriod < periodsPerYear; subPeriod++) {
            compareValue = compareValue * (1 + compareReturnRate / periodsPerYear);
          }
          compareValue += compareMonthlyAmount * 12 / periodsPerYear;
          compareContributions += compareMonthlyAmount * 12 / periodsPerYear;
        }

        compareInterest = compareValue - compareContributions;
        compareData.push({
          period,
          value: compareValue,
          contributions: compareContributions,
          interest: compareInterest,
          year: period / 12
        });
      }
      setComparisonData(compareData);
    } else {
      setComparisonData(null);
    }

    // Goal calculation
    const goal = parseFloat(goalAmount || 0);
    const ageNow = parseInt(currentAge || 30);
    const ageTarget = parseInt(targetAge || 65);
    const yearsToGoal = ageTarget - ageNow;
    const monthsToGoal = yearsToGoal * 12;

    let yearsToReachGoal = null;
    for (let i = 0; i < data.length; i++) {
      if (data[i].value >= goal) {
        yearsToReachGoal = data[i].year;
        break;
      }
    }

    const finalValue = data[data.length - 1]?.value || 0;
    const finalContributions = data[data.length - 1]?.contributions || 0;
    const finalInterest = data[data.length - 1]?.interest || 0;

    const result = {
      data,
      summary: {
        finalValue,
        finalContributions,
        finalInterest,
        interestPercentage: (finalInterest / finalContributions) * 100,
        effectiveAnnualReturn: effectiveAnnualReturn * 100,
        yearsToReachGoal,
        isGoalAchieved: finalValue >= goal,
        goalProgress: goal > 0 ? (finalValue / goal) * 100 : 0,
        totalPeriods: periods
      },
      metrics: {
        monthlyContribution: monthlyContributionAmount,
        annualReturn: annualReturnRate * 100,
        principal: principalAmount,
        timePeriod: periods / 12
      }
    };

    setCompoundingData(result);
  };

  const calculateRuleOf72 = (returnRate) => {
    return 72 / returnRate;
  };

  const getMilestones = () => {
    if (!compoundingData) return [];
    
    const milestones = [];
    const data = compoundingData.data;
    const doublingTime = calculateRuleOf72(parseFloat(inputs.annualReturn));
    
    // Find doubling milestones
    let doubleCount = 1;
    let lastDoubleValue = parseFloat(inputs.principal);
    
    for (let i = 0; i < data.length; i++) {
      const currentValue = data[i].value;
      if (currentValue >= lastDoubleValue * 2) {
        milestones.push({
          type: "double",
          period: data[i].period,
          year: data[i].year,
          value: currentValue,
          label: `${doubleCount}x Double`,
          description: `Investment doubled to ${doubleCount * 2}x original`
        });
        lastDoubleValue = currentValue;
        doubleCount++;
      }
      
      // Add $100k milestones
      const milestoneAmounts = [100000, 250000, 500000, 1000000, 2500000, 5000000];
      for (const amount of milestoneAmounts) {
        if (currentValue >= amount && !milestones.some(m => m.value === amount)) {
          milestones.push({
            type: "amount",
            period: data[i].period,
            year: data[i].year,
            value: amount,
            label: `$${(amount / 1000).toFixed(0)}k`,
            description: `Reached $${amount.toLocaleString()}`
          });
        }
      }
    }
    
    return milestones.slice(0, 10); // Limit to 10 milestones
  };

  const resetCalculator = () => {
    setInputs({
      principal: "10000",
      monthlyContribution: "500",
      annualReturn: "8",
      timePeriod: "20",
      compoundingFrequency: "annually",
      inflationRate: "2.5",
      taxRate: "15",
      fees: "0.25",
      contributionGrowth: "3",
      comparePrincipal: "5000",
      compareMonthly: "250",
      compareReturn: "6",
      goalAmount: "500000",
      currentAge: "30",
      targetAge: "65"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!compoundingData) return;
    
    const headers = "Period,Year,Portfolio Value,Total Contributions,Interest Earned,Inflation Adjusted Value\n";
    const rows = compoundingData.data.map(point => 
      `${point.period},${point.year.toFixed(1)},$${point.value.toFixed(2)},$${point.contributions.toFixed(2)},$${point.interest.toFixed(2)},$${point.realValue.toFixed(2)}`
    ).join('\n');
    
    const csvContent = headers + rows;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'compounding-growth.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!compoundingData) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      summary: compoundingData.summary
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { annualReturn: "6", compoundingFrequency: "annually", timePeriod: "30" },
      moderate: { annualReturn: "8", compoundingFrequency: "monthly", timePeriod: "25" },
      aggressive: { annualReturn: "10", compoundingFrequency: "monthly", timePeriod: "20" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} compounding preset applied`, "info");
  };

  const timePreset = (preset) => {
    const presets = {
      short: { timePeriod: "10" },
      medium: { timePeriod: "20" },
      long: { timePeriod: "30" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset}-term time preset applied`, "info");
  };

  // Chart Data
  const growthChartData = compoundingData ? {
    labels: compoundingData.data.map(d => 
      timeframe === "months" ? `Month ${d.period}` : `Year ${d.year.toFixed(1)}`
    ),
    datasets: [
      {
        label: 'Portfolio Value',
        data: compoundingData.data.map(d => d.value),
        borderColor: "#19692C",
        backgroundColor: "rgba(25, 105, 44, 0.1)",
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#19692C",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2
      },
      {
        label: 'Total Contributions',
        data: compoundingData.data.map(d => d.contributions),
        borderColor: "#FFD93D",
        backgroundColor: "rgba(255, 217, 61, 0.1)",
        fill: false,
        borderDash: [5, 5],
        tension: 0.4
      },
      ...(comparisonData ? [{
        label: 'Comparison Portfolio',
        data: comparisonData.map(d => d.value),
        borderColor: "#E53E3E",
        backgroundColor: "rgba(229, 62, 62, 0.1)",
        fill: false,
        borderDash: [3, 3],
        tension: 0.4
      }] : [])
    ]
  } : null;

  const breakdownChartData = compoundingData ? {
    labels: ['Initial Principal', 'Total Contributions', 'Interest Earned'],
    datasets: [{
      data: [
        parseFloat(inputs.principal),
        compoundingData.summary.finalContributions - parseFloat(inputs.principal),
        compoundingData.summary.finalInterest
      ],
      backgroundColor: ["#19692C", "#2B8C3A", "#28A745"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const milestoneData = getMilestones();

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Compounding Visualizer</h1>
        </div>
        <p>See the power of compound interest and watch your money grow over time</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "visualizer" ? "active" : ""}`}
                onClick={() => setActiveTab("visualizer")}
              >
                <i className="fas fa-chart-line"></i>
                {isMobile ? 'Visualizer' : 'Growth Visualizer'}
              </button>
              <button 
                className={`tab ${activeTab === "comparison" ? "active" : ""}`}
                onClick={() => setActiveTab("comparison")}
              >
                <i className="fas fa-balance-scale"></i>
                {isMobile ? 'Compare' : 'Scenario Compare'}
              </button>
              <button 
                className={`tab ${activeTab === "milestones" ? "active" : ""}`}
                onClick={() => setActiveTab("milestones")}
              >
                <i className="fas fa-flag"></i>
                {isMobile ? 'Milestones' : 'Growth Milestones'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Investment Strategy:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Timeframe Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${timeframe === "years" ? "active" : ""}`}
                onClick={() => setTimeframe("years")}
              >
                <i className="fas fa-calendar"></i>
                View in Years
              </button>
              <button 
                className={`mode-btn ${timeframe === "months" ? "active" : ""}`}
                onClick={() => setTimeframe("months")}
              >
                <i className="fas fa-calendar-alt"></i>
                View in Months
              </button>
            </div>
            <div className="mode-description">
              {timeframe === "years" 
                ? "Long-term view showing annual growth patterns"
                : "Detailed monthly view for precise tracking"
              }
            </div>
          </div>

          {/* Time Period Presets */}
          <div className="presets-section">
            <h4>Time Horizon:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => timePreset('short')}>
                10 Years
              </button>
              <button className="preset-btn moderate" onClick={() => timePreset('medium')}>
                20 Years
              </button>
              <button className="preset-btn aggressive" onClick={() => timePreset('long')}>
                30 Years
              </button>
            </div>
          </div>

          {/* Visualizer Tab */}
          {activeTab === "visualizer" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-seedling"></i>
                    Investment Basics
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Initial Investment</label>
                      <input 
                        type="number" 
                        name="principal" 
                        value={inputs.principal} 
                        onChange={handleChange}
                        placeholder="10000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar-plus"></i> Monthly Contribution</label>
                      <input 
                        type="number" 
                        name="monthlyContribution" 
                        value={inputs.monthlyContribution} 
                        onChange={handleChange}
                        placeholder="500"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Annual Return (%)</label>
                      <input 
                        type="number" 
                        name="annualReturn" 
                        value={inputs.annualReturn} 
                        onChange={handleChange}
                        placeholder="8"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-clock"></i> Time Period ({timeframe === "months" ? "Months" : "Years"})</label>
                      <input 
                        type="number" 
                        name="timePeriod" 
                        value={inputs.timePeriod} 
                        onChange={handleChange}
                        placeholder={timeframe === "months" ? "120" : "20"}
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-cogs"></i>
                    Compounding Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-repeat"></i> Compounding Frequency</label>
                      <select name="compoundingFrequency" value={inputs.compoundingFrequency} onChange={handleChange}>
                        <option value="annually">Annually</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="monthly">Monthly</option>
                        <option value="daily">Daily</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Contribution Growth (%)</label>
                      <input 
                        type="number" 
                        name="contributionGrowth" 
                        value={inputs.contributionGrowth} 
                        onChange={handleChange}
                        placeholder="3"
                        step="0.1"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Advanced Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Inflation Rate (%)</label>
                      <input 
                        type="number" 
                        name="inflationRate" 
                        value={inputs.inflationRate} 
                        onChange={handleChange}
                        placeholder="2.5"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Tax Rate (%)</label>
                      <input 
                        type="number" 
                        name="taxRate" 
                        value={inputs.taxRate} 
                        onChange={handleChange}
                        placeholder="15"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Annual Fees (%)</label>
                      <input 
                        type="number" 
                        name="fees" 
                        value={inputs.fees} 
                        onChange={handleChange}
                        placeholder="0.25"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Comparison Tab */}
          {activeTab === "comparison" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-balance-scale"></i>
                  Scenario Comparison
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-dollar-sign"></i> Compare: Initial Investment</label>
                    <input 
                      type="number" 
                      name="comparePrincipal" 
                      value={inputs.comparePrincipal} 
                      onChange={handleChange}
                      placeholder="5000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-calendar-plus"></i> Compare: Monthly Contribution</label>
                    <input 
                      type="number" 
                      name="compareMonthly" 
                      value={inputs.compareMonthly} 
                      onChange={handleChange}
                      placeholder="250"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Compare: Annual Return (%)</label>
                    <input 
                      type="number" 
                      name="compareReturn" 
                      value={inputs.compareReturn} 
                      onChange={handleChange}
                      placeholder="6"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Milestones Tab */}
          {activeTab === "milestones" && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-bullseye"></i>
                  Goal Tracking
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-bullseye"></i> Goal Amount</label>
                    <input 
                      type="number" 
                      name="goalAmount" 
                      value={inputs.goalAmount} 
                      onChange={handleChange}
                      placeholder="500000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-user"></i> Current Age</label>
                    <input 
                      type="number" 
                      name="currentAge" 
                      value={inputs.currentAge} 
                      onChange={handleChange}
                      placeholder="30"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-flag"></i> Target Age</label>
                    <input 
                      type="number" 
                      name="targetAge" 
                      value={inputs.targetAge} 
                      onChange={handleChange}
                      placeholder="65"
                    />
                  </div>
                </div>
                
                {compoundingData && compoundingData.summary.yearsToReachGoal && (
                  <div className="risk-results">
                    <h4>Goal Projection</h4>
                    <div className="risk-metrics">
                      <div className="risk-metric">
                        <span className="metric-label">Years to Reach Goal:</span>
                        <span className="metric-value">{compoundingData.summary.yearsToReachGoal.toFixed(1)}</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Age When Achieved:</span>
                        <span className="metric-value">
                          {parseInt(inputs.currentAge) + Math.ceil(compoundingData.summary.yearsToReachGoal)}
                        </span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Goal Progress:</span>
                        <span className="metric-value">{compoundingData.summary.goalProgress.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Growth History
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start visualizing to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit positive">
                          <i className="fas fa-arrow-up"></i>
                          ${calc.summary.finalValue.toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-chart-line"></i> Return: {calc.summary.interestPercentage.toFixed(1)}%</span>
                        <span><i className="fas fa-percentage"></i> APR: {calc.summary.effectiveAnnualReturn.toFixed(1)}%</span>
                        <span><i className="fas fa-coins"></i> Interest: ${calc.summary.finalInterest.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "visualizer" || activeTab === "comparison" || activeTab === "milestones") && (
          <div className="right-panel">
            {activeTab === "visualizer" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-chart-line"></i>
                    Compound Growth Visualization
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {compoundingData ? (
                  <>
                    {/* Key Metrics */}
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value positive">
                          ${compoundingData.summary.finalValue.toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          Final Portfolio Value
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${compoundingData.summary.finalInterest.toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-coins"></i>
                          Total Interest Earned
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {compoundingData.summary.interestPercentage.toFixed(1)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Return on Contributions
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {calculateRuleOf72(parseFloat(inputs.annualReturn)).toFixed(1)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-hourglass-half"></i>
                          Doubling Time (Years)
                        </div>
                      </div>
                    </div>

                    {/* Detailed Results */}
                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-piggy-bank"></i> Total Contributions:</span>
                        <span>${compoundingData.summary.finalContributions.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-rocket"></i> Effective Annual Return:</span>
                        <span>{compoundingData.summary.effectiveAnnualReturn.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calendar"></i> Total Time Period:</span>
                        <span>{(compoundingData.summary.totalPeriods / 12).toFixed(1)} years</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-bar"></i> Compounding Frequency:</span>
                        <span>{inputs.compoundingFrequency.charAt(0).toUpperCase() + inputs.compoundingFrequency.slice(1)}</span>
                      </div>
                    </div>

                    {/* Growth Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-project-diagram"></i>
                        Compound Growth Over Time
                      </h3>
                      <Line 
                        data={growthChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `${context.dataset.label}: $${context.parsed.y.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
                                }
                              }
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString(undefined, {maximumFractionDigits: 0});
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Portfolio Breakdown */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Portfolio Composition
                      </h3>
                      <Doughnut 
                        data={breakdownChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'right'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your investment details to see compounding magic</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "comparison" && compoundingData && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-balance-scale"></i>
                  Scenario Comparison
                </h3>

                {/* Comparison Summary */}
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: '#19692C' }}>
                      ${compoundingData.summary.finalValue.toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-chart-line"></i>
                      Main Scenario
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: '#E53E3E' }}>
                      ${comparisonData ? comparisonData[comparisonData.length - 1]?.value.toLocaleString(undefined, {maximumFractionDigits: 0}) : '0'}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-balance-scale"></i>
                      Comparison
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value" style={{ 
                      color: compoundingData.summary.finalValue > (comparisonData ? comparisonData[comparisonData.length - 1]?.value : 0) ? '#28A745' : '#E53E3E' 
                    }}>
                      ${(compoundingData.summary.finalValue - (comparisonData ? comparisonData[comparisonData.length - 1]?.value : 0)).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-difference"></i>
                      Difference
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {comparisonData ? (
                        ((compoundingData.summary.finalValue / comparisonData[comparisonData.length - 1]?.value - 1) * 100).toFixed(1)
                      ) : '0'}%
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-percentage"></i>
                      Performance Gap
                    </div>
                  </div>
                </div>

                {/* Comparison Insights */}
                <div className="risk-summary">
                  <h4>Key Insights</h4>
                  <div className="scenario-list">
                    <div className="scenario-item">
                      <div className="scenario-name">Return Advantage</div>
                      <div className="scenario-outcome">
                        {parseFloat(inputs.annualReturn) - parseFloat(inputs.compareReturn || 0)}% higher returns
                      </div>
                    </div>
                    <div className="scenario-item">
                      <div className="scenario-name">Contribution Impact</div>
                      <div className="scenario-outcome">
                        ${parseFloat(inputs.monthlyContribution) - parseFloat(inputs.compareMonthly || 0)}/month difference
                      </div>
                    </div>
                    <div className="scenario-item">
                      <div className="scenario-name">Starting Advantage</div>
                      <div className="scenario-outcome">
                        ${parseFloat(inputs.principal) - parseFloat(inputs.comparePrincipal || 0)} initial difference
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "milestones" && compoundingData && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-flag"></i>
                  Growth Milestones
                </h3><br/>
                
                {/* Milestones List */}
                <div className="scenario-comparison">
                  {milestoneData.length > 0 ? (
                    milestoneData.map((milestone, index) => (
                      <div key={index} className="scenario-item">
                        <div className="scenario-name">
                          <i className={`fas fa-${milestone.type === 'double' ? 'sync' : 'bullseye'}`}></i>
                          {milestone.label}
                        </div>
                        <div className="scenario-outcome">{milestone.description}</div>
                        <div className="scenario-stats">
                          <div className="breakdown-item">
                            <span>Reached at:</span>
                            <span>Year {milestone.year.toFixed(1)}</span>
                          </div>
                          <div className="breakdown-item">
                            <span>Portfolio Value:</span>
                            <span>${milestone.value.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state">
                      <i className="fas fa-flag fa-2x"></i>
                      <p>No milestones reached within the selected time period</p>
                    </div>
                  )}
                </div>

                {/* Rule of 72 */}
                <div className="risk-summary">
                  <h4>The Rule of 72</h4>
                  <div className="risk-metrics">
                    <div className="risk-metric">
                      <span className="metric-label">Doubling Time:</span>
                      <span className="metric-value">
                        {calculateRuleOf72(parseFloat(inputs.annualReturn)).toFixed(1)} years
                      </span>
                    </div>
                    <div className="risk-metric">
                      <span className="metric-label">Your money will double:</span>
                      <span className="metric-value">
                        {Math.floor(parseFloat(inputs.timePeriod) / calculateRuleOf72(parseFloat(inputs.annualReturn)))} times
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default CompoundingVisualizerCalculator;